import { useEffect, useRef, useState, type CSSProperties, type MouseEvent, type ReactNode } from "react";
import { Link } from 'react-router-dom';

/* ── ICONS (inline SVG to avoid import deps) ── */
type IconProps = {
  d: string;
  size?: number;
  className?: string;
  strokeWidth?: number;
  style?: CSSProperties;
};

const Icon = ({ d, size = 20, className = "", strokeWidth = 1.5, style = {} }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none"
    stroke="currentColor" strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round"
    className={className} style={style}>
    <path d={d} />
  </svg>
);

const icons = {
  globe: "M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 0c-2.5 3-4 6.5-4 10s1.5 7 4 10m0-20c2.5 3 4 6.5 4 10s-1.5 7-4 10M2 12h20",
  check: "M20 6L9 17l-5-5",
  x: "M18 6L6 18M6 6l12 12",
  zap: "M13 2L3 14h9l-1 8 10-12h-9l1-8z",
  credit: "M2 6a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6zm0 5h20",
  clock: "M12 2a10 10 0 1 0 0 20A10 10 0 0 0 12 2zm0 6v4l3 3",
  alert: "M12 9v4m0 4h.01M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z",
  package: "M16.5 9.4l-9-5.19M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16zM3.27 6.96L12 12.01l8.73-5.05M12 22.08V12",
  users: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2m22 0v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75",
  dollar: "M12 2v20M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6",
  network: "M9 3H5a2 2 0 0 0-2 2v4m6-6h10a2 2 0 0 1 2 2v4M9 3v18m0 0h10a2 2 0 0 0 2-2V9M9 21H5a2 2 0 0 1-2-2V9m0 0h18",
  file: "M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zm4 6H14V2",
  eye: "M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8zm11-3a3 3 0 1 0 0 6 3 3 0 0 0 0-6z",
  layers: "M12 2L2 7l10 5 10-5-10-5zM2 17l10 5 10-5M2 12l10 5 10-5",
  monitor: "M20 3H4a1 1 0 0 0-1 1v13a1 1 0 0 0 1 1h16a1 1 0 0 0 1-1V4a1 1 0 0 0-1-1zM8 21h8m-4-4v4",
  target: "M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20zm0-6a4 4 0 1 0 0-8 4 4 0 0 0 0 8zm0-2a2 2 0 1 0 0-4 2 2 0 0 0 0 4z",
  refresh: "M23 4v6h-6M1 20v-6h6m-.93-9A10 10 0 0 1 21.93 12a10 10 0 0 1-17.86 7",
  msg: "M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z",
  help: "M9.09 9a3 3 0 0 1 5.83 1c0 2-3 3-3 3m.08 4h.01M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20z",
  arrow: "M5 12h14m-7-7 7 7-7 7",
  settings: "M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6zm8.188-3A8.953 8.953 0 0 0 21 12a9 9 0 0 0-.812-3.766l-2.455.818A6.5 6.5 0 0 1 18 12a6.5 6.5 0 0 1-.267 1.948l2.455.818zM12 3a9 9 0 0 0-3.766.812l.818 2.455A6.5 6.5 0 0 1 12 6a6.5 6.5 0 0 1 1.948.267l.818-2.455A9 9 0 0 0 12 3z",
  shield: "M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z",
  bar: "M12 20V10m6 10V4M6 20v-4",
  trending: "M23 6l-9.5 9.5-5-5L1 18",
} as const;

type IconName = keyof typeof icons;
type IconCard = { icon: IconName; title: string; desc: string };
type Particle = {
  x: number;
  y: number;
  vx: number;
  vy: number;
  r: number;
  pulse: number;
};

/* ── Particle Canvas ── */
type ParticleCanvasProps = {
  color?: string;
  count?: number;
  maxDist?: number;
};

const ParticleCanvas = ({ color = "#ff5d22", count = 70, maxDist = 130 }: ParticleCanvasProps) => {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let w = 0;
    let h = 0;
    let pts: Particle[] = [];
    let raf = 0;
    const init = () => {
      w = canvas.width = canvas.offsetWidth;
      h = canvas.height = canvas.offsetHeight;
      pts = Array.from({ length: count }, () => ({
        x: Math.random() * w, y: Math.random() * h,
        vx: (Math.random() - 0.5) * 0.5, vy: (Math.random() - 0.5) * 0.5,
        r: Math.random() * 1.8 + 0.5,
        pulse: Math.random() * Math.PI * 2,
      }));
    };
    const hex2rgb = (hex: string): [number, number, number] => [
      parseInt(hex.slice(1, 3), 16),
      parseInt(hex.slice(3, 5), 16),
      parseInt(hex.slice(5, 7), 16),
    ];
    const [r,g,b] = hex2rgb(color);
    const draw = () => {
      ctx.clearRect(0, 0, w, h);
      pts.forEach(p => {
        p.x += p.vx; p.y += p.vy; p.pulse += 0.02;
        if (p.x < 0) p.x = w; if (p.x > w) p.x = 0;
        if (p.y < 0) p.y = h; if (p.y > h) p.y = 0;
      });
      for (let i = 0; i < pts.length; i++) {
        for (let j = i + 1; j < pts.length; j++) {
          const d = Math.hypot(pts[i].x - pts[j].x, pts[i].y - pts[j].y);
          if (d < maxDist) {
            const alpha = (1 - d / maxDist) * 0.25;
            ctx.beginPath();
            ctx.strokeStyle = `rgba(${r},${g},${b},${alpha})`;
            ctx.lineWidth = 0.7;
            ctx.moveTo(pts[i].x, pts[i].y);
            ctx.lineTo(pts[j].x, pts[j].y);
            ctx.stroke();
          }
        }
      }
      pts.forEach(p => {
        const pr = p.r * (1 + 0.3 * Math.sin(p.pulse));
        const grad = ctx.createRadialGradient(p.x, p.y, 0, p.x, p.y, pr * 3);
        grad.addColorStop(0, `rgba(${r},${g},${b},0.9)`);
        grad.addColorStop(1, `rgba(${r},${g},${b},0)`);
        ctx.beginPath();
        ctx.arc(p.x, p.y, pr * 3, 0, Math.PI * 2);
        ctx.fillStyle = grad;
        ctx.fill();
      });
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    const ro = new ResizeObserver(init);
    ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [color, count, maxDist]);
  return <canvas ref={ref} style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none" }} />;
};

/* ── 3D Grid Floor ── */
type GridFloorProps = {
  opacity?: number;
};

const GridFloor = ({ opacity = 0.07 }: GridFloorProps) => {
  const ref = useRef<HTMLCanvasElement | null>(null);
  useEffect(() => {
    const canvas = ref.current;
    if (!canvas) return;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;

    let w = 0;
    let h = 0;
    let raf = 0;
    let t = 0;
    const init = () => { w = canvas.width = canvas.offsetWidth; h = canvas.height = canvas.offsetHeight; };
    const draw = () => {
      t += 0.003;
      ctx.clearRect(0, 0, w, h);
      const cols = 20, rows = 14;
      const cw = w / cols, ch = h / rows;
      for (let i = 0; i <= cols; i++) {
        const x = i * cw;
        const wave = Math.sin(i * 0.4 + t) * 8;
        ctx.beginPath();
        ctx.strokeStyle = `rgba(227,30,36,${opacity * (0.5 + 0.5 * Math.sin(i * 0.3 + t))})`;
        ctx.lineWidth = 0.5;
        ctx.moveTo(x, 0);
        ctx.lineTo(x, h + wave);
        ctx.stroke();
      }
      for (let j = 0; j <= rows; j++) {
        const y = j * ch;
        const wave = Math.sin(j * 0.5 + t) * 5;
        ctx.beginPath();
        ctx.strokeStyle = `rgba(227,30,36,${opacity * (0.5 + 0.5 * Math.cos(j * 0.3 + t))})`;
        ctx.lineWidth = 0.5;
        ctx.moveTo(0, y + wave);
        ctx.lineTo(w, y + wave);
        ctx.stroke();
      }
      raf = requestAnimationFrame(draw);
    };
    init(); draw();
    const ro = new ResizeObserver(init);
    ro.observe(canvas);
    return () => { cancelAnimationFrame(raf); ro.disconnect(); };
  }, [opacity]);
  return <canvas ref={ref} style={{ position:"absolute", inset:0, width:"100%", height:"100%", pointerEvents:"none" }} />;
};

/* ── Orb ── */
type OrbProps = {
  style?: CSSProperties;
  color: string;
  size: number;
  animDur: number;
  animDelay?: number;
};

const Orb = ({ style, color, size, animDur, animDelay = 0 }: OrbProps) => (
  <div style={{
    position:"absolute", borderRadius:"50%", filter:`blur(${size*0.35}px)`,
    width:size, height:size, background:color, opacity:0.15,
    animation:`orbFloat ${animDur}s ease-in-out ${animDelay}s infinite`,
    ...style,
  }} />
);

/* ── Reveal wrapper ── */
type RevealProps = {
  children: ReactNode;
  delay?: number;
  style?: CSSProperties;
  className?: string;
};

const Reveal = ({ children, delay = 0, style = {}, className = "" }: RevealProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const [vis, setVis] = useState(false);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) { setVis(true); obs.disconnect(); } }, { threshold: 0.1 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={className} style={{
      opacity: vis ? 1 : 0, transform: vis ? "translateY(0) scale(1)" : "translateY(32px) scale(0.97)",
      transition: `opacity 0.7s ease ${delay}s, transform 0.7s ease ${delay}s`,
      ...style,
    }}>
      {children}
    </div>
  );
};

/* ── Tilt card ── */
type TiltCardProps = {
  children: ReactNode;
  className?: string;
  style?: CSSProperties;
};

const TiltCard = ({ children, className = "", style = {} }: TiltCardProps) => {
  const ref = useRef<HTMLDivElement | null>(null);
  const handleMove = (e: MouseEvent<HTMLDivElement>) => {
    if (!ref.current) return;
    const r = ref.current.getBoundingClientRect();
    const x = ((e.clientX - r.left) / r.width - 0.5) * 14;
    const y = ((e.clientY - r.top) / r.height - 0.5) * -14;
    ref.current.style.transform = `perspective(800px) rotateY(${x}deg) rotateX(${y}deg) translateY(-6px)`;
  };
  const handleLeave = () => {
    if (!ref.current) return;
    ref.current.style.transform = "perspective(800px) rotateY(0) rotateX(0) translateY(0)";
  };
  return (
    <div ref={ref} className={className} style={{ transition:"transform 0.3s ease", transformStyle:"preserve-3d", ...style }}
      onMouseMove={handleMove} onMouseLeave={handleLeave}>
      {children}
    </div>
  );
};

/* ── FAQ Item ── */
type FAQItemProps = {
  q: string;
  a: string;
  idx: number;
};

const FAQItem = ({ q, a, idx }: FAQItemProps) => {
  const [open, setOpen] = useState(false);
  return (
    <Reveal delay={idx * 0.08}>
      <div onClick={() => setOpen(!open)} style={{
        background: open ? "linear-gradient(135deg,#0f0f0f,#1a1a1a)" : "#fff",
        border: `1px solid ${open ? "rgba(227,30,36,0.5)" : "#e8e8e8"}`,
        borderRadius:12, padding:"16px 20px", cursor:"pointer",
        transition:"all 0.35s ease", boxShadow: open ? "0 8px 40px rgba(227,30,36,0.15)" : "0 2px 12px rgba(0,0,0,0.05)",
        marginBottom:10,
      }}>
        <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", gap:16 }}>
          <span style={{ fontFamily:"'DM Sans',sans-serif", fontWeight:700, fontSize:14, color: open ? "#fff" : "#0f0f0f", letterSpacing:"-0.01em" }}>
            {q}
          </span>
          <div style={{
            width:28, height:28, borderRadius:"50%", background: open ? "#ff5d22" : "#f4f4f4",
            display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0,
            transition:"all 0.3s ease", transform: open ? "rotate(45deg)" : "rotate(0)",
          }}>
            <Icon d="M12 5v14M5 12h14" size={12} className="" style={{ color: open ? "#fff" : "#666" }} />
          </div>
        </div>
        <div style={{
          overflow:"hidden", maxHeight: open ? 150 : 0, transition:"max-height 0.4s ease",
          marginTop: open ? 12 : 0,
        }}>
          <p style={{ fontFamily:"'DM Sans',sans-serif", fontSize:13, lineHeight:1.6, color:"rgba(255,255,255,0.72)", margin:0 }}>
            {a}
          </p>
        </div>
      </div>
    </Reveal>
  );
};

/* ── DATA ── */
const D = {
  stats: [
    { val:"10+", unit:"Days", label:"Average partner onboarding cycle reduced" },
    { val:"60%", unit:"", label:"Less manual billing reconciliation effort" },
    { val:"3x", unit:"", label:"Faster vendor and service launch cycles" },
  ],
  problems: [
    { icon:"credit", title:"Consolidated Billing Pressure", desc:"Multi-vendor, multi-partner billing creates leakage when usage, discounts, margins, and invoices live in separate systems." },
    { icon:"clock", title:"Slow Vendor Launches", desc:"New cloud, SaaS, AI, security, and infrastructure offers take too long to onboard, price, publish, and operationalize." },
    { icon:"alert", title:"Channel Operations Drag", desc:"Manual partner onboarding, approvals, entitlements, and provisioning workflows make it hard to scale thousands of partners." },
    { icon:"package", title:"Marketplace Sprawl", desc:"Disconnected catalogs and portals prevent distributors from launching unified marketplaces for partners and vendors." },
  ],
  comparison: [
    { f:"Vendor onboarding workflows", ours:"Governed and repeatable", theirs:"Manual setup" },
    { f:"Partner ecosystem operations", ours:"Multi-tier by design", theirs:"Fragmented" },
    { f:"Consolidated channel billing", ours:"Automated", theirs:"Spreadsheet-heavy" },
    { f:"Marketplace launch capability", ours:"Built in", theirs:"Custom project" },
    { f:"Consumption and revenue visibility", ours:"Real time", theirs:"Delayed reports" },
  ],
  whyFeatures: [
    "Automated vendor, partner, and billing reconciliation",
    "White-label partner portals for multi-tier channel ecosystems",
    "Granular pricing, margin, and discount controls by partner tier",
    "Real-time consumption, renewal, and revenue dashboards",
    "Open APIs for ERP, CRM, PSA, hyperscaler, and vendor integrations",
  ],
  pillars: [
    { icon:"users", title:"Partner Lifecycle Management", desc:"Onboard, tier, enable, support, and measure partners through governed workflows from one operating layer." },
    { icon:"dollar", title:"Channel Pricing Control", desc:"Manage margins, incentives, volume pricing, promotions, and partner-specific rules without spreadsheet dependency." },
    { icon:"network", title:"Vendor & Marketplace Orchestration", desc:"Connect hyperscalers, ISVs, security vendors, and service providers under one distributable catalog." },
  ],
  features: [
    { icon:"file", title:"Consolidated Channel Invoicing", desc:"Generate accurate invoices across vendors, partners, recurring services, usage, credits, and renewals without manual intervention." },
    { icon:"eye", title:"Consumption Intelligence", desc:"Track usage, partner performance, margin movement, and cloud consumption growth across the ecosystem." },
    { icon:"layers", title:"Multi-Tier Partner Support", desc:"Manage distributors, sub-distributors, channel partners, MSPs, and enterprise accounts within a single hierarchy." },
    { icon:"monitor", title:"Cloud Marketplace Builder", desc:"Launch branded marketplaces for partners with curated catalogs, bundles, approvals, and self-serve ordering." },
    { icon:"target", title:"Partner Quote & Order Workflows", desc:"Let partners configure offers, request approvals, submit orders, and move into provisioning with live catalog pricing." },
    { icon:"refresh", title:"Renewal & Lifecycle Automation", desc:"Automate renewal alerts, co-terming, upgrades, downgrades, and lifecycle actions across recurring services." },
  ],
  faq: [
    { q:"Is this platform built for cloud distributors?", a:"Yes. It is designed for distributors such as Redington, TD Synnex, Ingram Micro, Westcon, Crayon, Supertron, and similar channel-led businesses managing vendors, partners, marketplaces, billing, and recurring services." },
    { q:"How quickly can a distributor launch?", a:"Most implementations begin with a focused marketplace, billing, or partner onboarding rollout. Our team handles catalog setup, vendor integrations, partner portal configuration, and operational workflows end to end." },
    { q:"Can we white-label marketplaces and portals for partners?", a:"Fully. Partners can access branded portals, curated catalogs, tier-specific pricing, approvals, and ordering workflows while the underlying platform stays invisible." },
    { q:"Do you support multi-vendor and multi-tier environments?", a:"Yes. The platform is purpose-built for multi-vendor, multi-tier distribution models across hyperscalers, ISVs, security vendors, SaaS providers, and partner-led services." },
  ],
} satisfies {
  stats: Array<{ val: string; unit: string; label: string }>;
  problems: IconCard[];
  comparison: Array<{ f: string; ours: string; theirs: string }>;
  whyFeatures: string[];
  pillars: IconCard[];
  features: IconCard[];
  faq: Array<{ q: string; a: string }>;
};

/* ── MAIN PAGE ── */
export default function CloudDistributionPage() {
  return (
    <div className="bg-white overflow-x-hidden">
      <style>{`
        @keyframes orbFloat { 0%,100%{transform:translate(0,0) scale(1)}33%{transform:translate(40px,-30px) scale(1.08)}66%{transform:translate(-25px,20px) scale(0.95)} }
        @keyframes rotateSlow { from{transform:rotate(0deg)} to{transform:rotate(360deg)} }
        @keyframes pulse3d { 0%,100%{box-shadow:0 0 0 0 rgba(227,30,36,0.4)} 50%{box-shadow:0 0 0 20px rgba(227,30,36,0)} }
        @keyframes shimmer { 0%{background-position:-200% center} 100%{background-position:200% center} }
        @keyframes float { 0%,100%{transform:translateY(0)} 50%{transform:translateY(-12px)} }
        @keyframes scanline { 0%{transform:translateY(-100%)} 100%{transform:translateY(100vh)} }
        .card-3d { transition:transform 0.3s ease,box-shadow 0.3s ease }
        .card-3d:hover { transform:translateY(-8px);box-shadow:0 32px 64px rgba(0,0,0,0.18) }
      `}</style>

      {/* ─────────── HERO (LIGHT) ─────────── */}
      <section className="relative min-h-screen flex flex-col justify-center bg-gradient-to-b from-white via-slate-50 to-white overflow-hidden px-6 pb-12" style={{ paddingTop: "80px" }}>
        <ParticleCanvas count={80} color="#ff5d22" />
        <GridFloor opacity={0.04} />
        <Orb style={{ top:"-5%", right:"-8%" }} color="#ff5d22" size={500} animDur={18} />
        <Orb style={{ bottom:"-10%", left:"-5%" }} color="#3b82f6" size={400} animDur={22} animDelay={3} />

        <div className="relative z-20 max-w-6xl mx-auto w-full grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-black/6 text-slate-700 text-[9px] font-black uppercase tracking-widest mb-5 border border-slate-200">
                <svg width={8} height={8} viewBox="0 0 10 10"><circle cx={5} cy={5} r={5} fill="#ff5d22" /></svg>
                CLOUD DISTRIBUTION PLATFORM
              </span>
            </Reveal>

            <Reveal delay={0.08}>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-black tracking-tighter leading-[1.08] text-slate-950 mb-5">
                Enable More Partners.{" "}
                <span className="bg-gradient-to-r from-[#ff5d22] to-red-500 bg-clip-text text-transparent">
                  Launch More Services.
                </span>
                {" "}Grow More Revenue.
              </h1>
            </Reveal>

            <Reveal delay={0.14}>
              <p className="text-base text-slate-600 leading-relaxed mb-7 max-w-lg font-medium">
                The digital commerce platform that helps cloud distributors automate operations, expand partner ecosystems and monetize recurring services at scale.
              </p>
            </Reveal>

            <Reveal delay={0.20}>
              <div className="flex flex-wrap gap-3">
                <Link to="/contact">
                  <button className="px-7 py-3 rounded-lg bg-[#ff5d22] text-white font-black text-[10px] uppercase tracking-widest border-none cursor-pointer hover:bg-[#d41820] transition-all duration-300" style={{ animation:"pulse3d 3s ease infinite" }}>
                    Get Started Today
                  </button>
                </Link>
                <Link to="/solutions">
                  <button className="px-7 py-3 rounded-lg bg-white text-slate-700 font-black text-[10px] uppercase tracking-widest border border-slate-300 cursor-pointer hover:bg-slate-50 transition-all duration-300">
                    Explore Platform
                  </button>
                </Link>
              </div>
            </Reveal>
          </div>

          {/* Stats cluster */}
          <div className="grid gap-3">
            {D.stats.map((s, i) => (
              <Reveal key={i} delay={0.1 + i * 0.08}>
                <TiltCard className="bg-white border border-slate-200 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
                  <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22]/40 to-transparent" />
                  <div className="flex items-center gap-4">
                    <div>
                      <div className="text-4xl font-black text-[#2563EB] leading-none">{s.val}</div>
                      <div className="text-[9px] text-slate-400 font-black uppercase tracking-wider mt-0.5">{s.unit || "metric"}</div>
                    </div>
                    <div className="w-px h-10 bg-slate-200" />
                    <div className="text-xs text-slate-600 font-medium leading-relaxed">{s.label}</div>
                  </div>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── PROBLEMS ─────────── */}
      <section className="relative bg-slate-950 py-20 px-6 overflow-hidden">
        <ParticleCanvas count={50} color="#ff5d22" />
        <Orb style={{ top:"15%", right:"-8%" }} color="#ff5d22" size={350} animDur={20} />

        <div className="relative z-20 max-w-7xl mx-auto">
          <div className="text-center mb-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
                Why Distributors Need Scale
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-white mb-3">
                Cloud Distribution Breaks When Ecosystem Operations Stay Manual
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p className="text-sm text-white/50 max-w-xl mx-auto leading-relaxed font-medium">
                The operational constraints that slow vendor onboarding, partner activation, marketplace launches, billing consolidation, and consumption growth.
              </p>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {D.problems.map((p, i) => (
              <Reveal key={i} delay={i * 0.08}>
                <TiltCard className="bg-white/3 border border-white/8 rounded-lg p-6 backdrop-blur-xl relative overflow-hidden h-full">
                  <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-transparent via-[#ff5d22]/50 to-transparent" />
                  <div className="w-10 h-10 rounded-lg bg-[#ff5d22]/15 border border-[#ff5d22]/30 flex items-center justify-center mb-3">
                    <Icon d={icons[p.icon]} size={18} style={{ color:"#ff5d22" }} />
                  </div>
                  <h3 className="text-base font-black text-white mb-2 tracking-tight">{p.title}</h3>
                  <p className="text-xs text-white/55 leading-relaxed font-medium">{p.desc}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── COMPARISON ─────────── */}
      <section className="relative bg-slate-50 py-20 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-[radial-gradient(rgba(227,30,36,0.03)_1px,transparent_1px)] bg-[length:30px_30px]" />
        <Orb style={{ top:"10%", right:"5%" }} color="#ff5d22" size={280} animDur={18} />

        <div className="relative z-20 max-w-6xl mx-auto">
          <div className="text-center mb-12">
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                See the Difference
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-0">
                Our Platform vs.{" "}
                <span className="text-[#ff5d22]">Generic Portals</span>
              </h2>
            </Reveal>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Generic */}
            <Reveal delay={0.1}>
              <div className="bg-white border border-slate-200 rounded-2xl p-6 shadow-sm">
                <div className="flex items-center gap-2 mb-4 pb-4 border-b border-slate-100">
                  <div className="w-2 h-2 rounded-full bg-slate-300" />
                  <span className="text-[9px] font-black uppercase tracking-widest text-slate-400">Generic Portals</span>
                </div>
                {D.comparison.map((c, i) => (
                  <div key={i} className={`flex items-center justify-between py-2.5 text-xs ${i < D.comparison.length-1 ? 'border-b border-slate-50' : ''}`}>
                    <span className="font-bold text-slate-700">{c.f}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-red-50 flex items-center justify-center">
                        <Icon d={icons.x} size={8} style={{ color:"#ef4444" }} strokeWidth={2.5} />
                      </div>
                      <span className="text-slate-400 font-bold">{c.theirs}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>

            {/* Ours */}
            <Reveal delay={0.16}>
              <div className="bg-gradient-to-br from-slate-950 to-slate-900 border border-[#ff5d22]/30 rounded-2xl p-6 relative overflow-hidden shadow-lg">
                <div className="absolute top-0 left-0 right-0 h-px bg-gradient-to-r from-transparent via-[#ff5d22] to-transparent" />
                <div className="flex items-center justify-between mb-4 pb-4 border-b border-white/8 relative">
                  <div className="flex items-center gap-2">
                    <div className="w-2 h-2 rounded-full bg-[#ff5d22] shadow-lg shadow-[#ff5d22]/50" />
                    <span className="text-[9px] font-black uppercase tracking-widest text-white/60">Our Platform</span>
                  </div>
                  <span className="text-[8px] font-black uppercase tracking-widest text-[#ff5d22] flex items-center gap-1">
                    <Icon d={icons.zap} size={8} style={{ color:"#ff5d22" }} /> Recommended
                  </span>
                </div>
                {D.comparison.map((c, i) => (
                  <div key={i} className={`flex items-center justify-between py-2.5 text-xs ${i < D.comparison.length-1 ? 'border-b border-white/6' : ''} relative`}>
                    <span className="font-bold text-white/85">{c.f}</span>
                    <div className="flex items-center gap-2">
                      <div className="w-4 h-4 rounded-full bg-[#ff5d22]/20 flex items-center justify-center">
                        <Icon d={icons.check} size={8} style={{ color:"#ff5d22" }} strokeWidth={2.5} />
                      </div>
                      <span className="text-slate-400 font-bold">{c.ours}</span>
                    </div>
                  </div>
                ))}
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* ─────────── WHY US ─────────── */}
      <section style={{ position:"relative", background:"#fff", padding:"80px 24px", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(0,0,0,0.025) 1px,transparent 1px)", backgroundSize:"28px 28px" }} />
        <Orb style={{ bottom:"0%", left:"0%" }} color="#ff5d22" size={350} animDur={22} />

        <div style={{ position:"relative", zIndex:2, maxWidth:1000, margin:"0 auto", display:"grid", gridTemplateColumns:"1fr 1fr", gap:48, alignItems:"center" }}>
          <div>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Purpose-Built for Cloud Distribution
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-tight mb-4">
                Built for the Way Cloud Distribution{" "}
                <span className="text-[#ff5d22]">Actually Works</span>
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p style={{ fontSize:15, color:"#6b7280", lineHeight:1.75 }}>
                Most platforms are built for simple commerce motions, not distribution ecosystems. Ours is designed for the realities of cloud distribution: vendor onboarding, multi-tier partner ecosystems, billing consolidation, marketplace operations, consumption growth, and partner-led revenue.
              </p>
            </Reveal>
          </div>

          <div style={{ display:"flex", flexDirection:"column", gap:8 }}>
            {D.whyFeatures.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="card-3d" style={{ display:"flex", alignItems:"center", gap:12, padding:"14px 16px", background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, boxShadow:"0 1px 8px rgba(0,0,0,0.04)", cursor:"default" }}>
                  <div style={{ width:32, height:32, borderRadius:8, background:"rgba(227,30,36,0.08)", border:"1px solid rgba(227,30,36,0.2)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
                    <Icon d={icons.check} size={14} style={{ color:"#ff5d22" }} strokeWidth={2.5} />
                  </div>
                  <span style={{ fontSize:13, fontWeight:600, color:"#1f2937", lineHeight:1.5 }}>{f}</span>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── PILLARS ─────────── */}
      <section style={{ position:"relative", background:"#08080c", padding:"80px 24px", overflow:"hidden" }}>
        <ParticleCanvas count={50} color="#3b82f6" />
        <Orb style={{ top:"5%", left:"15%" }} color="#ff5d22" size={400} animDur={20} />
        <GridFloor opacity={0.04} />

        <div style={{ position:"relative", zIndex:2, maxWidth:1100, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
                Where Distributors Gain Full Control
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter leading-tight text-white mb-3">
                Everything You Need.{" "}
                <span className="text-[#ff5d22]">Nothing You Don't.</span>
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p style={{ fontSize:14, color:"rgba(255,255,255,0.5)", maxWidth:520, margin:"0 auto", lineHeight:1.7 }}>
                Run vendor, partner, marketplace, billing, and recurring revenue operations from a single platform designed around distribution scale.
              </p>
            </Reveal>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:20 }}>
            {D.pillars.map((p, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <TiltCard style={{
                  background:"rgba(255,255,255,0.03)", border:"1px solid rgba(255,255,255,0.08)",
                  borderRadius:16, padding:"32px 24px", textAlign:"center", backdropFilter:"blur(16px)",
                  position:"relative", overflow:"hidden", height:"100%",
                }}>
                  <div style={{ position:"absolute", top:0, left:0, right:0, height:1, background:`linear-gradient(90deg,transparent,rgba(227,30,36,${0.3+i*0.15}),transparent)` }} />
                  <div style={{ position:"relative", display:"inline-block", marginBottom:20 }}>
                    <div style={{ position:"absolute", inset:-10, borderRadius:"50%", background:"rgba(227,30,36,0.12)", filter:"blur(12px)" }} />
                    <div style={{ position:"relative", width:56, height:56, borderRadius:14, background:"rgba(227,30,36,0.15)", border:"1px solid rgba(227,30,36,0.3)", display:"flex", alignItems:"center", justifyContent:"center" }}>
                      <Icon d={icons[p.icon]} size={22} style={{ color:"#ff5d22" }} />
                    </div>
                  </div>
                  <h3 style={{ fontSize:17, fontWeight:800, color:"#fff", marginBottom:10, fontFamily:"'Syne',sans-serif" }}>{p.title}</h3>
                  <p style={{ fontSize:13, color:"rgba(255,255,255,0.55)", lineHeight:1.7, margin:0 }}>{p.desc}</p>
                </TiltCard>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── GTM BANNER ─────────── */}
      <section style={{ position:"relative", background:"linear-gradient(135deg,#ff5d22 0%,#b91c1c 50%,#991b1b 100%)", padding:"60px 24px", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(255,255,255,0.08) 1px,transparent 1px)", backgroundSize:"24px 24px" }} />

        <div style={{ position:"relative", zIndex:2, maxWidth:800, margin:"0 auto", textAlign:"center" }}>
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full" style={{ background:"rgba(255,255,255,0.15)", color:"#fff", border:"1px solid rgba(255,255,255,0.3)", fontSize: "9px", fontWeight:600, letterSpacing: "0.1em", textTransform: "uppercase", marginBottom:16 }}>
              Vendor Launch Speed + Channel Simplicity
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-4">
              Launch Faster. Operate Leaner. Grow Smarter.
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:"rgba(255,255,255,0.85)", lineHeight:1.75, maxWidth:580, margin:"0 auto" }}>
              Reduce the time from vendor agreement to live partner-ready offer. Pre-built catalog templates, approval workflows, and marketplace storefronts help teams spend less time configuring and more time enabling the channel.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─────────── FEATURES GRID ─────────── */}
      <section style={{ position:"relative", background:"#fafafa", padding:"80px 24px", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(227,30,36,0.03) 1px,transparent 1px)", backgroundSize:"28px 28px" }} />

        <div style={{ position:"relative", zIndex:2, maxWidth:1200, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:48 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Platform Features
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-0">
                The Features That Help Distributors{" "}
                <span className="text-[#ff5d22]">Scale Ecosystems</span>
              </h2>
            </Reveal>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:16 }}>
            {D.features.map((f, i) => (
              <Reveal key={i} delay={i * 0.06}>
                <div className="card-3d" style={{
                  background:"#fff", border:"1px solid #e5e7eb", borderRadius:14, padding:"24px 20px",
                  position:"relative", overflow:"hidden", height:"100%",
                  boxShadow:"0 1px 12px rgba(0,0,0,0.03)",
                }}>
                  <div style={{ width:44, height:44, borderRadius:10, background:"rgba(227,30,36,0.07)", border:"1px solid rgba(227,30,36,0.15)", display:"flex", alignItems:"center", justifyContent:"center", marginBottom:14 }}>
                    <Icon d={icons[f.icon]} size={18} style={{ color:"#ff5d22" }} />
                  </div>
                  <h3 style={{ fontSize:15, fontWeight:800, color:"#111827", marginBottom:8, fontFamily:"'Syne',sans-serif" }}>{f.title}</h3>
                  <p style={{ fontSize:13, color:"#6b7280", lineHeight:1.65, margin:0 }}>{f.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* ─────────── CUSTOMIZATION ─────────── */}
      <section style={{ position:"relative", background:"#0a0a0e", padding:"80px 24px", overflow:"hidden" }}>
        <ParticleCanvas count={50} color="#8b5cf6" />
        <Orb style={{ top:"-5%", right:"-8%" }} color="#ff5d22" size={400} animDur={19} />

        <div style={{ position:"relative", zIndex:2, maxWidth:840, margin:"0 auto", textAlign:"center" }}>
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
              Customized Solutions
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-4 leading-tight">
              A Solution Stack{" "}
              <span className="text-[#ff5d22]">Built Around You</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:"rgba(255,255,255,0.55)", lineHeight:1.75, maxWidth:580, margin:"0 auto 40px" }}>
              No two cloud distributors operate the same way. Configure workflows, catalog structures, pricing logic, billing rules, and partner portals to match your business model exactly.
            </p>
          </Reveal>

          <Reveal delay={0.2}>
            <div style={{ display:"flex", justifyContent:"center", gap:10, flexWrap:"wrap" }}>
              {["Workflow Config","Catalog Builder","Pricing Engine","White-Label Portal"].map((tag, i) => (
                <span key={i} style={{ padding:"8px 16px", borderRadius:100, background:"rgba(255,255,255,0.06)", border:"1px solid rgba(255,255,255,0.12)", color:"rgba(255,255,255,0.7)", fontSize:12, fontWeight:600 }}>
                  {tag}
                </span>
              ))}
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─────────── CLARITY ─────────── */}
      <section style={{ position:"relative", background:"#fff", padding:"80px 24px", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(0,0,0,0.025) 1px,transparent 1px)", backgroundSize:"28px 28px" }} />
        <Orb style={{ top:"0%", right:"0%" }} color="#ff5d22" size={300} animDur={17} />

        <div style={{ position:"relative", zIndex:2, maxWidth:800, margin:"0 auto", textAlign:"center" }}>
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
              Cloud Ecosystems Are Complex. Distributors Choose Clarity.
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-4">
              One Platform.{" "}
              <span className="text-[#ff5d22]">Total Catalog Clarity.</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:"#6b7280", lineHeight:1.75 }}>
              Stop managing vendors, products, price books, entitlements, and partner catalogs across disconnected portals. Centralize the ecosystem, apply your distribution logic, and present clean marketplaces to partners at every tier.
            </p>
          </Reveal>
        </div>
      </section>

      {/* ─────────── CASE STUDY ─────────── */}
      <section style={{ position:"relative", background:"#fafafa", padding:"80px 24px", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(227,30,36,0.03) 1px,transparent 1px)", backgroundSize:"28px 28px" }} />

        <div style={{ position:"relative", zIndex:2, maxWidth:920, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:40 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Case Study
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-0">Solving Real Distribution Hurdles</h2>
            </Reveal>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16, marginBottom:20 }}>
            {[{ val:"200%", label:"Increase in partner onboarding speed" }, { val:"99%", label:"Billing accuracy rate post-implementation" }].map((m, i) => (
              <Reveal key={i} delay={i * 0.1}>
                <TiltCard style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:14, padding:"32px 24px", textAlign:"center", boxShadow:"0 2px 16px rgba(0,0,0,0.04)", position:"relative", overflow:"hidden" }}>
                  <div style={{ position:"absolute", top:0, left:0, right:0, height:2, background:"linear-gradient(90deg,#ff5d22,rgba(227,30,36,0.3))" }} />
                  <div style={{ fontSize:48, fontWeight:900, fontFamily:"'Syne',sans-serif", color:"#2563EB", lineHeight:1, marginBottom:8 }}>{m.val}</div>
                  <div style={{ fontSize:12, fontWeight:700, letterSpacing:"0.05em", textTransform:"uppercase", color:"#9ca3af" }}>{m.label}</div>
                </TiltCard>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.18}>
            <div style={{ background:"linear-gradient(135deg,#111,#1c1c1c)", borderRadius:16, padding:"36px 32px", border:"1px solid rgba(255,255,255,0.08)", position:"relative", overflow:"hidden" }}>
              <div style={{ position:"absolute", top:0, left:0, right:0, height:1, background:"linear-gradient(90deg,transparent,rgba(227,30,36,0.6),transparent)" }} />
              <div style={{ display:"flex", gap:16, alignItems:"flex-start", position:"relative" }}>
                <div style={{ width:40, height:40, borderRadius:10, background:"rgba(227,30,36,0.2)", display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0, marginTop:2 }}>
                  <Icon d={icons.bar} size={18} style={{ color:"#ff5d22" }} />
                </div>
                <p style={{ fontSize:14, color:"rgba(255,255,255,0.75)", lineHeight:1.75, margin:0 }}>
                  A mid-market cloud distributor managing 12 vendor relationships and hundreds of channel partners consolidated their operations onto our platform. Within 90 days, billing disputes dropped to near zero, partner activation time was cut in half, and the team reclaimed 30 hours per week previously lost to manual reconciliation.
                </p>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─────────── ACTIVATION ─────────── */}
      <section style={{ position:"relative", background:"#08080c", padding:"80px 24px", overflow:"hidden" }}>
        <ParticleCanvas count={70} />
        <Orb style={{ top:"5%", left:"10%" }} color="#ff5d22" size={450} animDur={20} />
        <GridFloor opacity={0.06} />

        <div style={{ position:"relative", zIndex:2, maxWidth:800, margin:"0 auto", textAlign:"center" }}>
          <Reveal>
            <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/8 border border-white/15 text-white/70 text-[9px] font-black uppercase tracking-widest mb-4">
              Your Distribution Marketplace, Activated
            </span>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter text-white mb-4 leading-tight">
              The Platform Behind{" "}
              <span className="text-[#ff5d22]">Hundreds of Cloud</span>
              {" "}Launches
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:"rgba(255,255,255,0.55)", lineHeight:1.75, maxWidth:580, margin:"0 auto 32px" }}>
              Join cloud distributors replacing fragile systems with a single platform for vendor expansion, partner-led growth, and recurring service monetization.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <Link to="/contact">
              <button style={{
                padding:"12px 32px", borderRadius:10, background:"#ff5d22",
                color:"#fff", fontWeight:700, fontSize:12, letterSpacing:"0.08em",
                textTransform:"uppercase", border:"none", cursor:"pointer",
                animation:"pulse3d 3s ease infinite", fontFamily:"'DM Sans',sans-serif",
                display:"inline-flex", alignItems:"center", gap:8,
              }}>
                Activate Your Marketplace
                <Icon d={icons.arrow} size={14} style={{ color:"#fff" }} strokeWidth={2} />
              </button>
            </Link>
          </Reveal>
        </div>
      </section>

      {/* ─────────── BILLING / TESTIMONIAL ─────────── */}
      <section style={{ position:"relative", background:"#fff", padding:"80px 24px", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(0,0,0,0.025) 1px,transparent 1px)", backgroundSize:"28px 28px" }} />
        <Orb style={{ bottom:"0%", right:"0%" }} color="#ff5d22" size={300} animDur={21} />

        <div style={{ position:"relative", zIndex:2, maxWidth:920, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:40 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                Billing Feature
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 className="text-3xl md:text-4xl lg:text-5xl font-black tracking-tighter mb-3">
                Consolidate Channel Billing{" "}
                <span className="text-[#ff5d22]">From Vendor Cost to Partner Invoice</span>
              </h2>
            </Reveal>
            <Reveal delay={0.14}>
              <p style={{ fontSize:14, color:"#6b7280", lineHeight:1.75, maxWidth:520, margin:"0 auto" }}>
                Eliminate revenue leakage caused by fragmented billing processes. Our billing engine handles consumption aggregation, vendor cost reconciliation, partner margins, credits, taxes, and invoice generation automatically.
              </p>
            </Reveal>
          </div>

          <Reveal delay={0.2}>
            <div style={{
              background:"linear-gradient(135deg,#111,#1c1c1c)", borderRadius:16, padding:"40px 36px",
              textAlign:"center", border:"1px solid rgba(255,255,255,0.06)", position:"relative", overflow:"hidden",
              boxShadow:"0 20px 60px rgba(0,0,0,0.15)",
            }}>
              <div style={{ position:"absolute", top:0, left:0, right:0, height:1, background:"linear-gradient(90deg,transparent,#ff5d22,transparent)" }} />
              <div style={{ position:"relative" }}>
                <div style={{ width:48, height:48, borderRadius:12, background:"rgba(227,30,36,0.2)", display:"flex", alignItems:"center", justifyContent:"center", margin:"0 auto 20px" }}>
                  <Icon d={icons.msg} size={20} style={{ color:"#ff5d22" }} />
                </div>
                <blockquote style={{ fontSize:18, fontStyle:"italic", color:"rgba(255,255,255,0.85)", fontWeight:400, lineHeight:1.65, maxWidth:520, margin:"0 auto 16px", fontFamily:"'DM Sans',sans-serif" }}>
                  "Before this platform, billing took our team four days every month. Now it takes four hours."
                </blockquote>
                <div style={{ display:"flex", alignItems:"center", justifyContent:"center", gap:8 }}>
                  <span style={{ fontSize:11, fontWeight:700, letterSpacing:"0.1em", textTransform:"uppercase", color:"rgba(255,255,255,0.35)" }}>Channel Operations Lead, Mid-Market Distributor</span>
                </div>
              </div>
            </div>
          </Reveal>
        </div>
      </section>

      {/* ─────────── FAQ ─────────── */}
      <section style={{ position:"relative", background:"#fafafa", padding:"80px 24px", overflow:"hidden" }}>
        <div style={{ position:"absolute", inset:0, backgroundImage:"radial-gradient(rgba(227,30,36,0.03) 1px,transparent 1px)", backgroundSize:"28px 28px" }} />
        <Orb style={{ top:"15%", left:"-8%" }} color="#ff5d22" size={280} animDur={19} />

        <div style={{ position:"relative", zIndex:2, maxWidth:700, margin:"0 auto" }}>
          <div style={{ textAlign:"center", marginBottom:40 }}>
            <Reveal>
              <span className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-[#ff5d22]/10 border border-[#ff5d22]/25 text-[#ff5d22] text-[9px] font-black uppercase tracking-widest mb-4">
                FAQ Built for Clarity
              </span>
            </Reveal>
            <Reveal delay={0.08}>
              <h2 style={{ fontSize:"clamp(28px,4vw,48px)", fontWeight:800, marginBottom:0, color:"#1f2937" }}>Frequently Asked Questions</h2>
            </Reveal>
          </div>

          {D.faq.map((item, i) => (
            <FAQItem key={i} q={item.q} a={item.a} idx={i} />
          ))}
        </div>
      </section>

      {/* ─────────── FINAL CTA ─────────── */}
      <section style={{ position:"relative", background:"#06060a", padding:"100px 24px", overflow:"hidden" }}>
        <ParticleCanvas count={90} />
        <GridFloor opacity={0.06} />
        <Orb style={{ top:"10%", left:"10%" }} color="#ff5d22" size={450} animDur={18} />
        <Orb style={{ bottom:"10%", right:"5%" }} color="#3b82f6" size={380} animDur={22} animDelay={4} />

        <div style={{ position:"relative", zIndex:2, maxWidth:780, margin:"0 auto", textAlign:"center" }}>
          <Reveal>
            <div style={{ fontSize:12, fontWeight:700, letterSpacing:"0.15em", textTransform:"uppercase", color:"rgba(255,255,255,0.4)", marginBottom:16 }}>
              Ready to Transform Your Business?
            </div>
          </Reveal>
          <Reveal delay={0.08}>
            <h2 style={{ fontSize:"clamp(32px,5vw,64px)", color:"#fff", fontWeight:800, marginBottom:16, lineHeight:1.05 }}>
              Ready to Transform Your{" "}
              <span style={{ color:"#ff5d22" }}>Cloud Distribution?</span>
            </h2>
          </Reveal>
          <Reveal delay={0.14}>
            <p style={{ fontSize:15, color:"rgba(255,255,255,0.55)", lineHeight:1.75, maxWidth:500, margin:"0 auto 32px" }}>
              Build the operating layer for vendor onboarding, partner enablement, marketplace growth, billing consolidation, and recurring service monetization.
            </p>
          </Reveal>
          <Reveal delay={0.2}>
            <div style={{ display:"flex", gap:12, justifyContent:"center", flexWrap:"wrap" }}>
              <Link to="/contact">
                <button style={{
                  padding:"12px 32px", borderRadius:10, background:"#ff5d22",
                  color:"#fff", fontWeight:700, fontSize:12, letterSpacing:"0.08em",
                  textTransform:"uppercase", border:"none", cursor:"pointer",
                  animation:"pulse3d 3s ease infinite", fontFamily:"'DM Sans',sans-serif",
                  display:"inline-flex", alignItems:"center", gap:8,
                }}>
                  Get Started Today
                  <Icon d={icons.arrow} size={14} style={{ color:"#fff" }} strokeWidth={2} />
                </button>
              </Link>
              <Link to="/solutions">
                <button style={{
                  padding:"12px 32px", borderRadius:10, background:"transparent",
                  color:"rgba(255,255,255,0.75)", fontWeight:700, fontSize:12,
                  letterSpacing:"0.08em", textTransform:"uppercase",
                  border:"1px solid rgba(255,255,255,0.18)", cursor:"pointer",
                  fontFamily:"'DM Sans',sans-serif", transition:"all 0.2s",
                }}>Explore Solutions</button>
              </Link>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}
